import React, { useRef, useCallback, useEffect, useState } from 'react';
import Editor from '@monaco-editor/react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const LANG_MAP = {
  javascript: 'javascript',
  typescript: 'typescript',
  python: 'python',
  go: 'go',
  rust: 'rust',
  java: 'java',
  c: 'c',
  cpp: 'cpp',
  csharp: 'csharp',
  ruby: 'ruby',
  php: 'php',
  swift: 'swift',
  kotlin: 'kotlin',
  html: 'html',
  css: 'css',
  json: 'json',
  yaml: 'yaml',
  sql: 'sql',
  markdown: 'markdown',
  xml: 'xml',
  shell: 'shell',
  bash: 'shell',
};

const EXT_MAP = {
  '.js': 'javascript', '.jsx': 'javascript', '.mjs': 'javascript',
  '.ts': 'typescript', '.tsx': 'typescript',
  '.py': 'python', '.pyw': 'python',
  '.go': 'go', '.rs': 'rust', '.java': 'java',
  '.c': 'c', '.h': 'c',
  '.cpp': 'cpp', '.cc': 'cpp', '.hpp': 'cpp',
  '.cs': 'csharp', '.rb': 'ruby', '.php': 'php',
  '.swift': 'swift', '.kt': 'kotlin',
  '.html': 'html', '.htm': 'html',
  '.css': 'css', '.scss': 'css',
  '.json': 'json', '.yaml': 'yaml', '.yml': 'yaml',
  '.sql': 'sql', '.md': 'markdown', '.xml': 'xml',
  '.sh': 'shell', '.bash': 'shell',
};

function detectLangFromFilename(filename) {
  if (!filename) return 'javascript';
  for (const [ext, lang] of Object.entries(EXT_MAP)) {
    if (filename.endsWith(ext)) return LANG_MAP[lang] || lang;
  }
  return 'plaintext';
}

export const CodeEditor = ({
  defaultValue = '',
  language: langProp,
  filename = 'untitled.js',
  height = '100%',
  onChange,
  readOnly = false,
  enableCompletion = true,
  onStatusChange,
}) => {
  const editorRef = useRef(null);
  const monacoRef = useRef(null);
  const abortRef = useRef(null);
  const debounceRef = useRef(null);
  const providerRef = useRef(null);
  const [completionStatus, setCompletionStatus] = useState('idle');

  const language = langProp || detectLangFromFilename(filename);

  const updateStatus = useCallback((status) => {
    setCompletionStatus(status);
    onStatusChange?.(status);
  }, [onStatusChange]);

  const fetchCompletion = useCallback(async (model, position) => {
    if (!enableCompletion) return null;

    const textUntilPosition = model.getValueInRange({
      startLineNumber: 1,
      startColumn: 1,
      endLineNumber: position.lineNumber,
      endColumn: position.column,
    });
    const fullText = model.getValue();
    const suffix = fullText.slice(textUntilPosition.length);

    if (textUntilPosition.trim().length < 3) return null;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    updateStatus('loading');

    try {
      const res = await fetch(`${API_URL}/api/autocomplete/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        signal: controller.signal,
        body: JSON.stringify({
          prefix: textUntilPosition,
          suffix: suffix,
          filepath: filename,
          language: language,
          cursor_line: position.lineNumber - 1,
          cursor_col: position.column - 1,
          max_tokens: 128,
          temperature: 0.0,
          stream: true,
        }),
      });

      if (!res.ok) {
        updateStatus('error');
        return null;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let completionText = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const parsed = JSON.parse(line.slice(6));
            if (parsed.type === 'delta') {
              completionText += parsed.text;
            } else if (parsed.type === 'done' && parsed.completion) {
              completionText = parsed.completion;
            } else if (parsed.type === 'error') {
              updateStatus('error');
              return null;
            }
          } catch {}
        }
      }

      if (completionText.trim()) {
        updateStatus('ready');
        return completionText;
      }
      updateStatus('idle');
      return null;
    } catch (e) {
      if (e.name !== 'AbortError') updateStatus('error');
      return null;
    }
  }, [enableCompletion, filename, language, updateStatus]);

  const handleEditorMount = useCallback((editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;

    // Define CodeFoundry dark theme
    monaco.editor.defineTheme('codefoundry-dark', {
      base: 'vs-dark',
      inherit: true,
      rules: [
        { token: 'comment', foreground: '6a737d', fontStyle: 'italic' },
        { token: 'keyword', foreground: 'ff7b72' },
        { token: 'string', foreground: 'a5d6ff' },
        { token: 'number', foreground: '79c0ff' },
        { token: 'type', foreground: 'ffa657' },
        { token: 'function', foreground: 'd2a8ff' },
        { token: 'variable', foreground: 'ffa657' },
      ],
      colors: {
        'editor.background': '#0d1117',
        'editor.foreground': '#e6edf3',
        'editor.lineHighlightBackground': '#161b22',
        'editor.selectionBackground': '#264f78',
        'editorCursor.foreground': '#58a6ff',
        'editor.inactiveSelectionBackground': '#1c3049',
        'editorLineNumber.foreground': '#484f58',
        'editorLineNumber.activeForeground': '#e6edf3',
        'editorIndentGuide.background': '#21262d',
        'editorIndentGuide.activeBackground': '#30363d',
        'editorGhostText.foreground': '#484f58',
        'editorSuggestWidget.background': '#161b22',
        'editorSuggestWidget.border': '#30363d',
        'editorSuggestWidget.selectedBackground': '#1c3049',
      },
    });

    monaco.editor.setTheme('codefoundry-dark');

    // Register inline completion provider
    if (enableCompletion) {
      providerRef.current = monaco.languages.registerInlineCompletionsProvider(language, {
        provideInlineCompletions: async (model, position) => {
          // Debounce: only trigger after user stops typing for 500ms
          return new Promise((resolve) => {
            clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(async () => {
              const text = await fetchCompletion(model, position);
              if (!text) {
                resolve({ items: [] });
                return;
              }
              resolve({
                items: [{
                  insertText: text,
                  range: {
                    startLineNumber: position.lineNumber,
                    startColumn: position.column,
                    endLineNumber: position.lineNumber,
                    endColumn: position.column,
                  },
                }],
              });
            }, 500);
          });
        },
        freeInlineCompletions: () => {},
      });
    }

    // Keyboard shortcut: Escape to dismiss inline suggestion
    editor.addAction({
      id: 'codefoundry.dismissSuggestion',
      label: 'Dismiss Suggestion',
      keybindings: [monaco.KeyCode.Escape],
      run: () => {
        abortRef.current?.abort();
        updateStatus('idle');
      },
    });
  }, [enableCompletion, language, fetchCompletion, updateStatus]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      clearTimeout(debounceRef.current);
      providerRef.current?.dispose();
    };
  }, []);

  return (
    <div style={{ height, width: '100%', position: 'relative' }} data-testid="code-editor-container">
      <Editor
        height="100%"
        language={language}
        defaultValue={defaultValue}
        theme="codefoundry-dark"
        onChange={onChange}
        onMount={handleEditorMount}
        options={{
          fontSize: 14,
          fontFamily: "'IBM Plex Mono', 'Fira Code', Consolas, monospace",
          fontLigatures: true,
          lineNumbers: 'on',
          minimap: { enabled: true, scale: 1 },
          scrollBeyondLastLine: false,
          wordWrap: 'on',
          tabSize: 2,
          insertSpaces: true,
          automaticLayout: true,
          suggestOnTriggerCharacters: true,
          quickSuggestions: true,
          inlineSuggest: { enabled: enableCompletion },
          readOnly,
          padding: { top: 16, bottom: 16 },
          smoothScrolling: true,
          cursorBlinking: 'smooth',
          cursorSmoothCaretAnimation: 'on',
          renderWhitespace: 'selection',
          bracketPairColorization: { enabled: true },
          guides: { bracketPairs: true, indentation: true },
        }}
        loading={
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            height: '100%', background: '#0d1117', color: '#484f58',
            fontFamily: 'IBM Plex Mono', fontSize: '0.875rem',
          }}>
            Loading editor...
          </div>
        }
      />
      {/* Completion status indicator */}
      {enableCompletion && completionStatus !== 'idle' && (
        <div
          data-testid="completion-status-indicator"
          style={{
            position: 'absolute',
            bottom: 8,
            right: 16,
            padding: '3px 10px',
            borderRadius: '4px',
            fontSize: '0.6875rem',
            fontFamily: 'IBM Plex Mono',
            background: completionStatus === 'loading' ? 'rgba(88, 166, 255, 0.15)'
              : completionStatus === 'error' ? 'rgba(248, 81, 73, 0.15)'
              : 'rgba(63, 185, 80, 0.15)',
            color: completionStatus === 'loading' ? '#58a6ff'
              : completionStatus === 'error' ? '#f85149'
              : '#3fb950',
            pointerEvents: 'none',
            transition: 'opacity 0.2s',
          }}
        >
          {completionStatus === 'loading' ? 'Completing...'
            : completionStatus === 'error' ? 'Completion error'
            : 'Suggestion ready (Tab to accept)'}
        </div>
      )}
    </div>
  );
};

export default CodeEditor;
